import React, { useEffect, useMemo, useState, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import {
  listExpertViews,
  getExpertViewMeta,
  getImageUrl,
  downloadExpertViewBlob,
} from '../api/expertViewService';
import './ResearchDesk.css';

const ANALYSIS_LABELS = {
  long: { label: 'Long', icon: '↗', cls: 'ev-pill--long' },
  short: { label: 'Short', icon: '↘', cls: 'ev-pill--short' },
  neutral: { label: 'Neutral', icon: '◎', cls: 'ev-pill--neutral' },
};

const fmtPrice = (v) =>
  v == null || v === ''
    ? '—'
    : Number(v).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TvIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const CalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

/**
 * Compute Risk:Reward.
 *   Long  : risk = entry - stoploss, reward = target - entry
 *   Short : risk = stoploss - entry, reward = entry - target
 * Returns { ratio: '1:2.35', risk, reward, valid }, or null if not computable.
 */
const calcRiskReward = ({ entryPrice, target, stoploss, analysisType }) => {
  const e = Number(entryPrice);
  const t = Number(target);
  const sl = Number(stoploss);
  if (!Number.isFinite(e) || !Number.isFinite(t) || !Number.isFinite(sl)) return null;

  let risk, reward;
  if (analysisType === 'short') {
    risk = sl - e;
    reward = e - t;
  } else {
    // long & neutral default to long-side math
    risk = e - sl;
    reward = t - e;
  }

  if (risk <= 0 || reward <= 0) {
    return { ratio: 'Invalid levels', risk, reward, valid: false };
  }

  const rr = reward / risk;
  return {
    ratio: `1 : ${rr.toFixed(2)}`,
    risk: risk.toFixed(2),
    reward: reward.toFixed(2),
    valid: true,
    favourable: rr >= 2,
  };
};

const ResearchDesk = () => {
  const [items, setItems]       = useState([]);
  const [meta, setMeta]         = useState({ strategyCategories: [], analysisTypes: [] });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Filters
  const [search, setSearch]     = useState('');
  const [category, setCategory] = useState('all');
  const [type, setType]         = useState('all');
  const [page, setPage]         = useState(1);
  const [pagination, setPagination] = useState({ pages: 1, total: 0 });

  // Lightbox
  const [activeItem, setActiveItem] = useState(null);

  const fetchMeta = useCallback(async () => {
    try {
      const { data } = await getExpertViewMeta();
      setMeta({
        strategyCategories: data.strategyCategories || [],
        analysisTypes: data.analysisTypes || [],
      });
    } catch {
      /* ignore */
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await listExpertViews({
        search: search.trim() || undefined,
        strategyCategory: category,
        analysisType: type,
        page,
        limit: 24,
      });
      setItems(data.items || []);
      setPagination(data.pagination || { pages: 1, total: 0 });
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [search, category, type, page]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const onSubmitFilter = (e) => {
    e.preventDefault();
    setPage(1);
    fetchList();
  };

  const onDownload = async (item) => {
    try {
      const res = await downloadExpertViewBlob(item._id);
      const blob = new Blob([res.data], { type: item.mimeType || 'image/jpeg' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ext = (item.mimeType || 'image/jpeg').split('/')[1] || 'jpg';
      a.href = url;
      a.download = `${item.symbol}_${item.analysisType}_${item._id}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + (err.response?.data?.message || err.message));
    }
  };

  const filteredCount = useMemo(() => items.length, [items]);

  const calendarUrl = (item) => {
    const y = new Date().getFullYear();
    const m = new Date().getMonth() + 1;
    const cats = item.categories || (item.category ? [item.category] : []);
    const normalized = cats.map((c) => String(c).toLowerCase());
    let col = 'fo';
    if (normalized.some((c) => c === 'n50' || c === 'nifty50' || c === 'nifty 50' || c === 'nifty-50')) col = 'nifty50';
    else if (normalized.some((c) => c === 'others' || c === 'cash')) col = 'cash';
    return `/cs-calendar?symbol=${encodeURIComponent(item.symbol)}&year=${y}&month=${m}&collection=${col}`;
  };

  return (
    <AppLayout pageBadge="Research Desk">
      <div className="ev-page">
        <div className="ev-header">
          <div>
            <h1 className="ev-title">Market analysis & trade ideas</h1>
            <p className="ev-subtitle">
              Curated charts shared by experts — filter, browse, and download.
            </p>
          </div>
        </div>

        {/* ── Filters ─────────────────────────────────────────── */}
        <form className="ev-filters glass-card" onSubmit={onSubmitFilter}>
          <div className="ev-filter-row">
            <div className="ev-filter">
              <label>Search</label>
              <input
                type="text"
                placeholder="Symbol, title, tag…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="ev-filter">
              <label>Strategy</label>
              <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
                <option value="all">All</option>
                {meta.strategyCategories.map((c) => (
                  <option key={c} value={c}>{c.replace(/([a-z])([A-Z])/g, '$1 $2')}</option>
                ))}
              </select>
            </div>

            <div className="ev-filter">
              <label>Type</label>
              <div className="ev-type-toggle">
                {['all', ...meta.analysisTypes].map((t) => (
                  <button
                    type="button"
                    key={t}
                    className={`ev-type-btn ${type === t ? 'active' : ''}`}
                    onClick={() => { setType(t); setPage(1); }}
                  >
                    {t === 'all' ? 'All' : ANALYSIS_LABELS[t]?.icon + ' ' + ANALYSIS_LABELS[t]?.label}
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" className="ev-btn-primary">Apply</button>
          </div>

          <div className="ev-meta-row">
            <span>{pagination.total} chart{pagination.total === 1 ? '' : 's'} found</span>
            {(search || category !== 'all' || type !== 'all') && (
              <button
                type="button"
                className="ev-btn-link"
                onClick={() => { setSearch(''); setCategory('all'); setType('all'); setPage(1); }}
              >
                Clear filters
              </button>
            )}
          </div>
        </form>

        {/* ── Grid ────────────────────────────────────────────── */}
        {loading && <div className="ev-loading">Loading charts…</div>}
        {error && <div className="ev-error">{error}</div>}

        {!loading && !error && items.length === 0 && (
          <div className="ev-empty glass-card">
            <div className="ev-empty-icon">📭</div>
            <h3>No charts yet</h3>
            <p>Try changing the filters, or check back soon — experts publish ideas regularly.</p>
          </div>
        )}

        <div className="ev-grid">
          {items.map((it) => {
            const a = ANALYSIS_LABELS[it.analysisType] || ANALYSIS_LABELS.neutral;
            return (
              <article key={it._id} className="ev-card glass-card lift-hover">
                <div
                  className="ev-card-img-wrap"
                  onClick={() => setActiveItem(it)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setActiveItem(it)}
                >
                  <img
                    src={getImageUrl(it.imageUrl)}
                    alt={it.title}
                    loading="lazy"
                    className="ev-card-img"
                  />
                  <span className={`ev-pill ${a.cls}`}>
                    {a.icon} {a.label}
                  </span>
                </div>

                <div className="ev-card-body">
                  <div className="ev-card-top">
                    <span className="ev-symbol-wrap">
                      <span className="ev-symbol">{it.symbol}</span>
                      <span className="ev-symbol-links">
                        <a
                          href={`https://in.tradingview.com/chart/?symbol=NSE%3A${it.symbol}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`View ${it.symbol} on TradingView`}
                          className="ev-sym-link ev-sym-link--tv"
                        >
                          <TvIcon />
                        </a>
                        <a
                          href={calendarUrl(it)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`View ${it.symbol} CS Calendar`}
                          className="ev-sym-link ev-sym-link--cal"
                        >
                          <CalIcon />
                        </a>
                      </span>
                    </span>
                    <span className="ev-strategy">{it.strategyCategory}</span>
                  </div>
                  <h3 className="ev-card-title">{it.title}</h3>

                  <div className="ev-price-row">
                    {it.entryPrice != null && (
                      <span className="ev-price ev-price--entry">
                        Entry: <b>{fmtPrice(it.entryPrice)}</b>
                      </span>
                    )}
                    {it.target != null && (
                      <span className="ev-price ev-price--target">
                        Target: <b>{fmtPrice(it.target)}</b>
                      </span>
                    )}
                    {it.stoploss != null && (
                      <span className="ev-price ev-price--sl">
                        SL: <b>{fmtPrice(it.stoploss)}</b>
                      </span>
                    )}
                  </div>

                  <div className="ev-card-foot">
                    <span className="ev-author">
                      by <b>{it.uploaderName || 'Expert'}</b> · {fmtDate(it.createdAt)}
                    </span>
                    <button
                      className="ev-icon-btn"
                      title="Download chart"
                      onClick={() => onDownload(it)}
                    >
                      ⬇
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* ── Pagination ──────────────────────────────────────── */}
        {pagination.pages > 1 && (
          <div className="ev-pagination">
            <button
              className="ev-btn-pag"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ← Prev
            </button>
            <span>Page {page} of {pagination.pages}</span>
            <button
              className="ev-btn-pag"
              disabled={page >= pagination.pages}
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
            >
              Next →
            </button>
          </div>
        )}

        {/* ── Lightbox ────────────────────────────────────────── */}
        {activeItem && (
          <div
            className="ev-lightbox"
            onClick={() => setActiveItem(null)}
            role="dialog"
            aria-modal="true"
          >
            <div className="ev-lightbox-content" onClick={(e) => e.stopPropagation()}>
              <button
                className="ev-lightbox-close"
                onClick={() => setActiveItem(null)}
                aria-label="Close"
              >
                ✕
              </button>

              <img
                src={getImageUrl(activeItem.imageUrl)}
                alt={activeItem.title}
                className="ev-lightbox-img"
              />

              <div className="ev-lightbox-meta">
                <div className="ev-lightbox-head">
                  <h2>{activeItem.title}</h2>
                  <span className={`ev-pill ${ANALYSIS_LABELS[activeItem.analysisType]?.cls}`}>
                    {ANALYSIS_LABELS[activeItem.analysisType]?.icon}{' '}
                    {ANALYSIS_LABELS[activeItem.analysisType]?.label}
                  </span>
                </div>

                <div className="ev-lightbox-info-grid">
                  <div><span>Symbol</span><b>{activeItem.symbol}</b></div>
                  <div><span>Strategy</span><b>{activeItem.strategyCategory}</b></div>
                  <div><span>Record</span><b>{fmtPrice(activeItem.recordPrice)}</b></div>
                  <div><span>Entry</span><b>{fmtPrice(activeItem.entryPrice)}</b></div>
                  <div><span>Target</span><b className="ev-pos">{fmtPrice(activeItem.target)}</b></div>
                  <div><span>Stop-loss</span><b className="ev-neg">{fmtPrice(activeItem.stoploss)}</b></div>
                </div>

                {(() => {
                  const rr = calcRiskReward(activeItem);
                  if (!rr) return null;
                  return (
                    <div className={`ev-rr-card ${rr.valid ? (rr.favourable ? 'ev-rr-card--good' : 'ev-rr-card--ok') : 'ev-rr-card--bad'}`}>
                      <div className="ev-rr-head">
                        <span className="ev-rr-icon">⚖️</span>
                        <span className="ev-rr-label">Risk : Reward</span>
                        <span className="ev-rr-ratio">{rr.ratio}</span>
                      </div>
                      {rr.valid && (
                        <div className="ev-rr-detail">
                          <span>Risk: <b className="ev-neg">₹{rr.risk}</b></span>
                          <span>Reward: <b className="ev-pos">₹{rr.reward}</b></span>
                          {rr.favourable && <span className="ev-rr-tag">✓ Favourable</span>}
                        </div>
                      )}
                      {!rr.valid && (
                        <div className="ev-rr-detail">
                          <span>Entry, target and stop-loss don't form a valid {activeItem.analysisType} setup.</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {activeItem.description && (
                  <p className="ev-lightbox-desc">{activeItem.description}</p>
                )}

                {activeItem.tags?.length > 0 && (
                  <div className="ev-tags">
                    {activeItem.tags.map((t) => (
                      <span key={t} className="ev-tag">#{t}</span>
                    ))}
                  </div>
                )}

                <div className="ev-lightbox-foot">
                  <span>
                    by <b>{activeItem.uploaderName || 'Expert'}</b> · {fmtDate(activeItem.createdAt)}
                  </span>
                  <button className="ev-btn-primary" onClick={() => onDownload(activeItem)}>
                    ⬇ Download chart
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default ResearchDesk;
